/**
 * Messaging v2 — typed wrapper around the operations REST API.
 *
 * Uses the shared `operationsClient` axios instance from `@/lib/api`,
 * which already injects the operations auth bearer and `x-tenant-id`
 * headers.
 */

import { apiGet, apiPost, apiPatch, apiPut, apiDelete, operationsClient } from '@/lib/api';
import type {
  AvailableNumber,
  Channel,
  ManagedChannel,
  CreateChannelPayload,
  UpdateChannelPayload,
  WebChatWidget,
  CreateWebChatWidgetPayload,
  UpdateWebChatWidgetPayload,
  WebChatEmbed,
  WebChatIdentitySecret,
  ClosePayload,
  CreateMessagingGroupPayload,
  CreateMessagingGroupResult,
  ComposeEmailPayload,
  EmailAttachmentDownload,
  EmailUploadedAttachment,
  ListMessagesParams,
  ListPhoneNumbersParams,
  ListThreadsParams,
  MessageItem,
  MessagingGroupOptions,
  OrderNumberPayload,
  OutboundNumberRoute,
  Paginated,
  PaginatedPhoneNumbers,
  PhoneNumber,
  ReassignPayload,
  SearchAvailableNumbersParams,
  MediaAttachmentRef,
  SendMessagePayload,
  SendMessageResult,
  SmsSenderLine,
  SetRestrictedPayload,
  SnoozePayload,
  ThreadDetail,
  ThreadListItem,
  ThreadWithContext,
  UpdatePhoneNumberPayload,
  UpdateTagsPayload,
} from './types';

// ─── URL builders ─────────────────────────────────────────────────────────────

const inboxUrl = {
  threads: () => '/api/messaging/inbox/threads',
  senderLines: () => '/api/messaging/inbox/sender-lines',
  thread: (id: string) => `/api/messaging/inbox/threads/${id}`,
  messages: (id: string) => `/api/messaging/inbox/threads/${id}/messages`,
  markRead: (id: string) => `/api/messaging/inbox/threads/${id}/mark-read`,
  snooze: (id: string) => `/api/messaging/inbox/threads/${id}/snooze`,
  close: (id: string) => `/api/messaging/inbox/threads/${id}/close`,
  reopen: (id: string) => `/api/messaging/inbox/threads/${id}/reopen`,
  aiAgent: (id: string) => `/api/messaging/inbox/threads/${id}/ai-agent`,
  section: (id: string) => `/api/messaging/inbox/threads/${id}/section`,
  reassign: (id: string) => `/api/messaging/inbox/threads/${id}/reassign`,
  tags: (id: string) => `/api/messaging/inbox/threads/${id}/tags`,
  restricted: (id: string) => `/api/messaging/inbox/threads/${id}/restricted`,
  uploadAttachment: () => '/api/messaging/inbox/attachments/upload',
  groupMessaging: () => '/api/messaging/inbox/group-messaging',
  groupMessagingOptions: () => '/api/messaging/inbox/group-messaging/options',
};

const inboxesUrl = {
  list: () => '/api/messaging/inboxes',
  manage: () => '/api/messaging/inboxes/manage',
  detail: (id: string) => `/api/messaging/inboxes/${id}`,
};

const phoneNumberUrl = {
  list: () => '/api/messaging/phone-numbers',
  available: () => '/api/messaging/phone-numbers/available',
  order: () => '/api/messaging/phone-numbers/order',
  detail: (id: string) => `/api/messaging/phone-numbers/${id}`,
  release: (id: string) => `/api/messaging/phone-numbers/${id}/release`,
};

// ─── Param helpers ────────────────────────────────────────────────────────────

/**
 * Serialize list params for `GET /inbox/threads`. An allow-list, so a new
 * `ListThreadsParams` field is silently dropped until it is added here —
 * which is exactly how the Escalated view once returned every open thread.
 */
export function buildThreadParams(p: ListThreadsParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.section) out.section = p.section;
  if (p.status) {
    out.status = Array.isArray(p.status) ? p.status.join(',') : p.status;
  }
  if (p.assignedUserId) out.assignedUserId = p.assignedUserId;
  if (p.unreadOnly !== undefined) out.unreadOnly = p.unreadOnly;
  if (p.escalatedOnly !== undefined) out.escalatedOnly = p.escalatedOnly;
  if (p.channel) out.channel = p.channel;
  if (p.phoneNumberId) out.phoneNumberId = p.phoneNumberId;
  if (p.inboxId) out.inboxId = p.inboxId;
  if (p.tag) out.tag = p.tag;
  if (p.search) out.search = p.search;
  if (p.page) out.page = p.page;
  if (p.pageSize) out.pageSize = p.pageSize;
  return out;
}

// ─── Threads / inbox ─────────────────────────────────────────────────────────

export const threadsApi = {
  composeSms: (payload: {
    inboxId: string;
    toE164: string;
    body: string;
    media?: MediaAttachmentRef[];
  }) =>
    apiPost<SendMessageResult & { threadId: string }>(
      operationsClient,
      '/api/messaging/inbox/compose-sms',
      payload,
    ),
  setSection: (id: string, section: 'INBOX' | 'OTHER') =>
    apiPatch<ThreadDetail, { section: 'INBOX' | 'OTHER' }>(operationsClient, inboxUrl.section(id), {
      section,
    }),
  list: (params: ListThreadsParams = {}) =>
    apiGet<Paginated<ThreadListItem>>(operationsClient, inboxUrl.threads(), {
      params: buildThreadParams(params),
    }),

  get: (id: string) => apiGet<ThreadWithContext>(operationsClient, inboxUrl.thread(id)),

  /** Find-or-create the SMS thread for a contact (customerId === contact.id). */
  openForCustomer: (customerId: string, phoneNumberId?: string) =>
    apiPost<{ threadId: string; status: string }, { customerId: string; phoneNumberId?: string }>(
      operationsClient,
      inboxUrl.threads(),
      { customerId, ...(phoneNumberId ? { phoneNumberId } : {}) },
    ),

  senderLines: (customerId: string) =>
    apiGet<SmsSenderLine[]>(operationsClient, inboxUrl.senderLines(), {
      params: { customerId },
    }),

  listMessages: (id: string, params: ListMessagesParams = {}) =>
    apiGet<Paginated<MessageItem>>(operationsClient, inboxUrl.messages(id), {
      params,
    }),

  send: (id: string, payload: SendMessagePayload) =>
    apiPost<SendMessageResult, SendMessagePayload>(
      operationsClient,
      inboxUrl.messages(id),
      payload,
    ),

  /**
   * Upload an outbound MMS media file (server-side proxy to S3). Returns a ref
   * the send call ships in `media[]`. Multipart is marked explicitly so axios
   * populates the boundary instead of defaulting to JSON (mirrors the email
   * attachment upload).
   */
  uploadAttachment: async (
    file: File,
    onProgress?: (ratio: number) => void,
  ): Promise<MediaAttachmentRef> => {
    const form = new FormData();
    form.append('file', file);
    const res = await operationsClient.post<MediaAttachmentRef>(inboxUrl.uploadAttachment(), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(onProgress
        ? {
            onUploadProgress: (event) => {
              if (event.total && event.total > 0) {
                onProgress(Math.min(1, event.loaded / event.total));
              }
            },
          }
        : {}),
    });
    return res.data;
  },

  markRead: (id: string) => apiPost<void>(operationsClient, inboxUrl.markRead(id), {}),

  snooze: (id: string, until: string) =>
    apiPost<ThreadDetail, SnoozePayload>(operationsClient, inboxUrl.snooze(id), {
      until,
    }),

  close: (id: string, closingNote?: string) =>
    apiPost<ThreadDetail, ClosePayload>(operationsClient, inboxUrl.close(id), {
      closingNote,
    }),

  reopen: (id: string) => apiPost<ThreadDetail>(operationsClient, inboxUrl.reopen(id), {}),

  setAiEnabled: (id: string, enabled: boolean) =>
    apiPost<ThreadDetail, { enabled: boolean }>(operationsClient, inboxUrl.aiAgent(id), {
      enabled,
    }),

  reassign: (id: string, toUserId: string, note?: string) =>
    apiPost<ThreadDetail, ReassignPayload>(operationsClient, inboxUrl.reassign(id), {
      toUserId,
      note,
    }),

  setTags: (id: string, tags: string[]) =>
    apiPatch<ThreadDetail, UpdateTagsPayload>(operationsClient, inboxUrl.tags(id), { tags }),

  setRestricted: (id: string, restricted: boolean) =>
    apiPost<ThreadDetail, SetRestrictedPayload>(operationsClient, inboxUrl.restricted(id), {
      restricted,
    }),
};

export const groupMessagingApi = {
  options: () => apiGet<MessagingGroupOptions>(operationsClient, inboxUrl.groupMessagingOptions()),
  create: (payload: CreateMessagingGroupPayload) =>
    apiPost<CreateMessagingGroupResult, CreateMessagingGroupPayload>(
      operationsClient,
      inboxUrl.groupMessaging(),
      payload,
    ),
};

// ─── Phone numbers ────────────────────────────────────────────────────────────

// ─── Channels (Inboxes) ────────────────────────────────────────────────────────

export const inboxesApi = {
  list: () => apiGet<Channel[]>(operationsClient, inboxesUrl.list()),
  /** Every channel incl. unreadable personal mailboxes — `channels:manage` only. */
  listManaged: () => apiGet<ManagedChannel[]>(operationsClient, inboxesUrl.manage()),
  create: (payload: CreateChannelPayload) =>
    apiPost<Channel, CreateChannelPayload>(operationsClient, inboxesUrl.list(), payload),
  update: (id: string, payload: UpdateChannelPayload) =>
    apiPatch<Channel, UpdateChannelPayload>(operationsClient, inboxesUrl.detail(id), payload),
  remove: (id: string) => apiDelete<void>(operationsClient, inboxesUrl.detail(id)),
};

// ─── Web chat widgets ──────────────────────────────────────────────────────────

const webchatUrl = {
  list: () => '/api/messaging/webchat/widgets',
  detail: (id: string) => `/api/messaging/webchat/widgets/${id}`,
  embed: (id: string) => `/api/messaging/webchat/widgets/${id}/embed`,
  rotateKey: (id: string) => `/api/messaging/webchat/widgets/${id}/rotate-key`,
  identitySecret: (id: string) => `/api/messaging/webchat/widgets/${id}/identity-secret`,
};

export const webchatWidgetsApi = {
  list: () => apiGet<WebChatWidget[]>(operationsClient, webchatUrl.list()),
  create: (payload: CreateWebChatWidgetPayload) =>
    apiPost<WebChatWidget, CreateWebChatWidgetPayload>(
      operationsClient,
      webchatUrl.list(),
      payload,
    ),
  update: (id: string, payload: UpdateWebChatWidgetPayload) =>
    apiPatch<WebChatWidget, UpdateWebChatWidgetPayload>(
      operationsClient,
      webchatUrl.detail(id),
      payload,
    ),
  remove: (id: string) => apiDelete<void>(operationsClient, webchatUrl.detail(id)),
  rotateKey: (id: string) =>
    apiPost<WebChatWidget, Record<string, never>>(operationsClient, webchatUrl.rotateKey(id), {}),
  embed: (id: string) => apiGet<WebChatEmbed>(operationsClient, webchatUrl.embed(id)),
  /** Mint a new identity signing secret. The response is the ONLY time it is disclosed. */
  rotateIdentitySecret: (id: string) =>
    apiPost<WebChatIdentitySecret, Record<string, never>>(
      operationsClient,
      webchatUrl.identitySecret(id),
      {},
    ),
};

export const phoneNumbersApi = {
  routing: () =>
    apiGet<OutboundNumberRoute[]>(operationsClient, '/api/messaging/phone-numbers/routing'),

  setRouting: (scopeKey: string, payload: Omit<OutboundNumberRoute, 'scopeKey'>) =>
    apiPut<OutboundNumberRoute, Omit<OutboundNumberRoute, 'scopeKey'>>(
      operationsClient,
      `/api/messaging/phone-numbers/routing/${encodeURIComponent(scopeKey)}`,
      payload,
    ),

  deleteRouting: (scopeKey: string) =>
    apiDelete<void>(
      operationsClient,
      `/api/messaging/phone-numbers/routing/${encodeURIComponent(scopeKey)}`,
    ),

  list: (params: ListPhoneNumbersParams = {}) =>
    apiGet<PaginatedPhoneNumbers>(operationsClient, phoneNumberUrl.list(), {
      params,
    }),

  listAll: async (params: Omit<ListPhoneNumbersParams, 'page' | 'pageSize'> = {}) => {
    const numbers: PhoneNumber[] = [];
    for (let page = 1; page <= 20; page += 1) {
      const result = await phoneNumbersApi.list({ ...params, page, pageSize: 200 });
      numbers.push(...result.data);
      if (!result.hasMore) break;
    }
    return numbers;
  },

  get: (id: string) => apiGet<PhoneNumber>(operationsClient, phoneNumberUrl.detail(id)),

  searchAvailable: (params: SearchAvailableNumbersParams = {}) =>
    apiGet<{ data: AvailableNumber[] }>(operationsClient, phoneNumberUrl.available(), { params }),

  order: (payload: OrderNumberPayload) =>
    apiPost<PhoneNumber, OrderNumberPayload>(operationsClient, phoneNumberUrl.order(), payload),

  update: (id: string, payload: UpdatePhoneNumberPayload) =>
    apiPatch<PhoneNumber, UpdatePhoneNumberPayload>(
      operationsClient,
      phoneNumberUrl.detail(id),
      payload,
    ),

  release: (id: string) => apiPost<void>(operationsClient, phoneNumberUrl.release(id), {}),
};

// ─── Email channel ──────────────────────────────────────────────────────────

const emailUrl = {
  attachment: (messageId: string, providerAttachmentId: string) =>
    `/messaging/email/messages/${messageId}/attachments/${encodeURIComponent(
      providerAttachmentId,
    )}`,
  compose: (channelAccountId: string) => `/messaging/email/accounts/${channelAccountId}/messages`,
  uploadAttachment: () => `/messaging/email/attachments/upload`,
};

export const emailApi = {
  /** Resolve a short-lived signed URL for an inbound email attachment. */
  attachmentUrl: (messageId: string, providerAttachmentId: string) =>
    apiGet<EmailAttachmentDownload>(
      operationsClient,
      emailUrl.attachment(messageId, providerAttachmentId),
    ),
  /**
   * Send an email from the given connected mailbox. Used by the inbox
   * reply path on email threads. The API enforces that the caller owns
   * (or has EMAIL_WRITE share access to) the mailbox; mismatch surfaces
   * as 403.
   */
  compose: (channelAccountId: string, payload: ComposeEmailPayload) =>
    apiPost<{ messageId: string }, ComposeEmailPayload>(
      operationsClient,
      emailUrl.compose(channelAccountId),
      payload,
    ),
  /**
   * Upload an outbound attachment ahead of compose. Server-side proxy
   * to S3 (the bucket has no browser-CORS grant). Returns a ref the
   * composer carries through to the compose payload.
   *
   * Browser-side cap matches the API's MulterFile interceptor at 25 MB
   * per file; the composer enforces a total cap of 25 MB across all
   * attachments to match Gmail / Outlook's per-message limit.
   *
   * `onProgress` reports 0–1 progress for the chip UI.
   */
  uploadAttachment: async (
    file: File,
    onProgress?: (ratio: number) => void,
  ): Promise<EmailUploadedAttachment> => {
    const form = new FormData();
    form.append('file', file);
    // Explicitly mark the request as multipart so axios doesn't fall
    // back to the client's default `Content-Type: application/json`
    // (which serializes the FormData to `{"file":{}}` and triggers the
    // server's "No file uploaded" guard). Setting `multipart/form-data`
    // without a boundary lets axios populate the boundary itself from
    // the FormData body. Reported in the post-deploy Playwright
    // verification of #438.
    const res = await operationsClient.post<EmailUploadedAttachment>(
      emailUrl.uploadAttachment(),
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        ...(onProgress
          ? {
              onUploadProgress: (event) => {
                if (event.total && event.total > 0) {
                  onProgress(Math.min(1, event.loaded / event.total));
                }
              },
            }
          : {}),
      },
    );
    return res.data;
  },
};

// ─── Unified export ───────────────────────────────────────────────────────────

export const messagingApi = {
  threads: threadsApi,
  groupMessaging: groupMessagingApi,
  phoneNumbers: phoneNumbersApi,
  email: emailApi,
};
